import { Modal, Button, Card, Spinner, Alert } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';

import { useAuthenticatedAxios } from '@/shared/services/axiosInstance';
import { createPost } from '@/shared/services/postApi';
import useUserCommunities from '@/hooks/useUserCommunities';
import CreatePostModal from '@/features/communities/components/CreatePostModal';

/**
 * Build a community route, falling back to the communities list when the slug
 * is missing so we never navigate to `/communities/undefined`.
 */
const communityPath = (slug, suffix = '') =>
    slug ? `/communities/${slug}${suffix}` : '/communities';

/**
 * Two-step modal that lets the user share content to one of their communities.
 *
 * Step 1 — Community picker (skipped when the user belongs to only one community)
 * Step 2 — CreatePostModal pre-filled with the shareContent
 */
const ShareToCommunityModal = ({ show, onHide, shareContent = {} }) => {
    const { communities, hasCommunities, loading, error } =
        useUserCommunities();
    const axiosInstance = useAuthenticatedAxios();
    const navigate = useNavigate();

    const [selectedCommunity, setSelectedCommunity] = useState(null);
    const [showPostModal, setShowPostModal] = useState(false);
    const [confirmation, setConfirmation] = useState(null);

    // Auto-select when user has exactly one community
    useEffect(() => {
        if (show && !loading && communities.length === 1) {
            setSelectedCommunity(communities[0]);
            setShowPostModal(true);
        }
    }, [show, loading, communities]);

    // Reset state when modal closes
    useEffect(() => {
        if (!show) {
            setSelectedCommunity(null);
            setShowPostModal(false);
            setConfirmation(null);
        }
    }, [show]);

    const handleCommunitySelect = (membership) => {
        setSelectedCommunity(membership);
        setShowPostModal(true);
    };

    const handlePostSubmit = useCallback(
        async (postData) => {
            const communityId =
                selectedCommunity?.community?._id ||
                selectedCommunity?.communityId;

            const result = await createPost(
                axiosInstance,
                {
                    communityId,
                    postType: postData.postType || 'discussion',
                    title: postData.title || '',
                    content: postData.content,
                    resourceUrl: postData.resourceUrl || '',
                    resourceType: postData.resourceType || '',
                },
                postData.image || null
            );

            if (
                result?.moderationStatus === 'blocked' ||
                result?.moderationStatus === 'pending_review'
            ) {
                return result;
            }

            // Show confirmation with link to the community
            const communityName =
                selectedCommunity?.community?.name || 'your community';
            const communitySlug =
                selectedCommunity?.community?.slug ||
                selectedCommunity?.communityId;

            setShowPostModal(false);
            setConfirmation({ communityName, communitySlug });

            return result;
        },
        [axiosInstance, selectedCommunity]
    );

    const handlePostModalHide = () => {
        setShowPostModal(false);
        // If confirmation is set, let it render instead of closing
        if (confirmation) return;
        // If user has multiple communities, go back to picker
        if (communities.length > 1) {
            setSelectedCommunity(null);
        } else {
            onHide();
        }
    };

    const handleViewGuidelines = useCallback(() => {
        const communitySlug =
            selectedCommunity?.community?.slug ||
            selectedCommunity?.communityId;
        onHide();
        navigate(communityPath(communitySlug, '?tab=guidelines'));
    }, [selectedCommunity, onHide, navigate]);

    // Confirmation screen after successful post
    if (confirmation) {
        return (
            <Modal
                show={show}
                onHide={onHide}
                centered
                aria-labelledby="share-confirmation-title"
            >
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title
                        as="h2"
                        id="share-confirmation-title"
                        className="text-primary fw-bold fs-4"
                    >
                        Shared!
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center py-4" role="status">
                    <i
                        className="bi bi-check-circle fs-1 text-success mb-3 d-block"
                        aria-hidden="true"
                    />
                    <p className="mb-3">
                        Your post has been shared to{' '}
                        <strong>{confirmation.communityName}</strong>.
                    </p>
                    <Link
                        to={communityPath(confirmation.communitySlug)}
                        className="btn btn-outline-primary rounded-pill"
                        onClick={onHide}
                    >
                        View Community
                    </Link>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0 justify-content-center">
                    <Button
                        variant="primary"
                        onClick={onHide}
                        className="px-4 rounded-pill"
                    >
                        Done
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    // CreatePostModal with pre-filled content
    if (showPostModal && selectedCommunity) {
        return (
            <CreatePostModal
                show={show}
                onHide={handlePostModalHide}
                onSubmit={handlePostSubmit}
                onViewGuidelines={handleViewGuidelines}
                initialPostType={shareContent?.postType}
                initialTitle={shareContent?.title}
                initialContent={shareContent?.content}
                initialResourceUrl={shareContent?.resourceUrl}
                initialResourceType={shareContent?.resourceType}
                key={`share-${selectedCommunity?.community?._id || selectedCommunity?.communityId}`}
            />
        );
    }

    // Community picker (step 1)
    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            aria-labelledby="share-to-community-title"
        >
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title
                    as="h2"
                    id="share-to-community-title"
                    className="text-primary fw-bold fs-4"
                >
                    Share to Community
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-3">
                {loading && (
                    <div
                        className="d-flex justify-content-center py-4"
                        role="status"
                    >
                        <Spinner animation="border" variant="primary" />
                        <span className="visually-hidden">
                            Loading communities...
                        </span>
                    </div>
                )}

                {error && (
                    <Alert variant="danger">
                        Failed to load your communities. Please try again.
                    </Alert>
                )}

                {!loading && !error && !hasCommunities && (
                    <div className="text-center py-4">
                        <p className="text-muted mb-3">
                            You need to join a community before you can share
                            posts.
                        </p>
                        <Link
                            to="/communities"
                            className="btn btn-primary rounded-pill"
                            onClick={onHide}
                        >
                            Browse Communities
                        </Link>
                    </div>
                )}

                {!loading && !error && hasCommunities && (
                    <>
                        <p className="text-muted mb-3">
                            Choose a community to share to:
                        </p>
                        <div className="d-flex flex-column gap-2">
                            {communities.map((membership) => {
                                const community = membership.community || {};
                                return (
                                    <Card
                                        key={membership._id}
                                        className="border cursor-pointer"
                                        role="button"
                                        tabIndex={0}
                                        onClick={() =>
                                            handleCommunitySelect(membership)
                                        }
                                        onKeyDown={(e) => {
                                            if (
                                                e.key === 'Enter' ||
                                                e.key === ' '
                                            ) {
                                                e.preventDefault();
                                                handleCommunitySelect(
                                                    membership
                                                );
                                            }
                                        }}
                                        aria-label={`Share to ${community.name || 'community'}`}
                                    >
                                        <Card.Body className="d-flex align-items-center gap-3 py-2">
                                            <i
                                                className="bi bi-people-fill fs-4 text-primary"
                                                aria-hidden="true"
                                            />
                                            <div>
                                                <strong>
                                                    {community.name ||
                                                        'Community'}
                                                </strong>
                                                <small className="d-block text-muted">
                                                    {membership.role}
                                                </small>
                                            </div>
                                            <i
                                                className="bi bi-chevron-right ms-auto text-muted"
                                                aria-hidden="true"
                                            />
                                        </Card.Body>
                                    </Card>
                                );
                            })}
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0 justify-content-center">
                <Button
                    variant="outline-primary"
                    onClick={onHide}
                    className="px-4 rounded-pill"
                >
                    Cancel
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

ShareToCommunityModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    shareContent: PropTypes.shape({
        postType: PropTypes.oneOf(['discussion', 'resource']),
        title: PropTypes.string,
        content: PropTypes.string,
        resourceUrl: PropTypes.string,
        resourceType: PropTypes.string,
    }),
};

export default ShareToCommunityModal;
